// src/pages/admin/UserMonitor.tsx
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, useMediaQuery, useTheme, Drawer, CircularProgress,
  Alert, Snackbar, Button, TextField, InputAdornment, Card, Chip, Avatar,
  Stack, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Pagination, IconButton, Collapse, Divider, LinearProgress, Badge,
} from '@mui/material';
import {
  Refresh, Search, Close, People, PersonAdd, Visibility,
  KeyboardArrowDown, KeyboardArrowUp, CheckCircle, Cancel,
  PhoneAndroid, Web, AttachMoney, TrendingUp, ShoppingCart,
  Visibility as VisibilityIcon, Star, Smartphone, NotificationsActive,
  AccountCircle, CreditCard, Receipt, Email as EmailIcon, Phone,
  Person, PersonOff, Group, Public, AppShortcut,
  Download,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useAppUserMonitorListQuery,
  useWebUserMonitorListQuery,
  AppUserEntry,
  WebUserEntry,
  AppUserMonitorRequest,
  WebUserMonitorRequest,
  USER_MONITOR_ENDPOINTS,
} from '../../features/admin/userMonitorApi.ts';

// ─── CSV Download Hook ────────────────────────────────────────────────────────

function useCSVDownload(endpoint: string) {
  const [loading, setLoading] = useState(false);

  const download = async (params: Record<string, unknown>, filename: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...params, download: 1 }),
        }
      );

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed:', err);
      alert('CSV download failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return { download, loading };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (v: string | number) => `AED ${(Number(v) || 0).toFixed(2)}`;
const pct  = (n: number)          => `${(Number(n) || 0).toFixed(1)}%`;

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const getInitials = (name: string) => {
  const p = (name || '').trim().split(' ').filter(Boolean);
  return p.length >= 2 ? `${p[0][0]}${p[1][0]}`.toUpperCase() : (name?.[0] || '?').toUpperCase();
};

const safeRating = (r: string | number | null | undefined) => {
  if (r === null || r === undefined || r === '') return '—';
  return String(r);
};

const ratingColor = (r: string | number | null | undefined) => {
  const s = String(r ?? '').toLowerCase();
  if (s.includes('excellent') || s.includes('a')) return '#00783e';
  if (s.includes('good')      || s.includes('b')) return '#2196f3';
  if (s.includes('average')   || s.includes('c')) return '#e65100';
  if (s.includes('poor')      || s.includes('d')) return '#c62828';
  return '#6d7175';
};

const planColor = (planName: string) => {
  const p = (planName || '').toLowerCase();
  if (p.includes('platinum')) return { bg: '#e8eaf6', color: '#3f51b5' };
  if (p.includes('gold'))     return { bg: '#fff8e1', color: '#f57f17' };
  if (p.includes('silver'))   return { bg: '#f5f5f5', color: '#616161' };
  return { bg: '#fafbfb', color: '#6d7175' };
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{ icon: React.ReactNode; bg: string; value: string | number; label: string; small?: boolean }> =
  ({ icon, bg, value, label, small }) => (
    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
      <Box sx={{ p: small ? 2 : 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: small ? 0.8 : 1, display: 'flex', flexShrink: 0 }}>{icon}</Box>
        <Box>
          <Typography variant={small ? 'h6' : 'h5'}
            sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.1, fontSize: small ? '16px' : undefined }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: small ? '11px' : '12px' }}>{label}</Typography>
        </Box>
      </Box>
    </Card>
  );

// ─────────────────────────────────────────────────────────────────────────────
// App User Detail Dialog
// ─────────────────────────────────────────────────────────────────────────────

const AppUserDetailDialog: React.FC<{
  user: AppUserEntry | null;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const [tab, setTab] = useState(0);

  if (!user) return null;
  const { profile, device, account, membership, wallet, referrals, today, period, performance, recent_orders } = user;
  const pc = planColor(membership.plan_name);

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '14px', maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 42, height: 42, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, fontSize: '16px' }}>
              {getInitials(profile.full_name)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>
                {profile.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">{profile.email} · #{profile.user_id}</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Chip size="small" label={account.status_label}
            sx={{ bgcolor: account.status === 1 ? '#d1f4e0' : '#ffebee', color: account.status === 1 ? '#00783e' : '#c62828', fontWeight: 600, fontSize: '11px' }} />
          <Chip size="small" label={membership.plan_name}
            sx={{ bgcolor: pc.bg, color: pc.color, fontWeight: 600, fontSize: '11px' }} />
          {device.app_installed && <Chip size="small" icon={<Smartphone sx={{ fontSize: '12px !important' }} />} label="App Installed" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '11px' }} />}
          {device.push_enabled  && <Chip size="small" icon={<NotificationsActive sx={{ fontSize: '12px !important' }} />} label="Push Enabled" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '11px' }} />}
          <Chip size="small" label={`Wallet: ${fmt$(wallet.balance)}`}
            sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700, fontSize: '11px' }} />
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mt: 1.5,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px', minHeight: 40, color: '#6d7175' },
          '& .Mui-selected': { color: '#202223', fontWeight: 600 },
          '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 2 },
        }}>
          <Tab label="Overview" />
          <Tab label="Performance" />
          <Tab label={`Orders (${recent_orders.length})`} />
          <Tab label="Device & Profile" />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>

        {/* Tab 0: Overview */}
        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Today</Typography>
                <Stack spacing={0.5}>
                  {[
                    { l: 'Orders',         v: today.orders },
                    { l: 'Delivered',      v: today.delivered,      c: '#00783e' },
                    { l: 'Cancelled',      v: today.cancelled,      c: today.cancelled > 0 ? '#c62828' : undefined },
                    { l: 'Spend',          v: fmt$(today.spend),    c: '#7b1fa2' },
                    { l: 'Product Views',  v: today.product_views   },
                    { l: 'Purchase Events',v: today.purchase_events },
                  ].map(({ l, v, c }) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Period ({period.from_date} → {period.to_date})</Typography>
                <Stack spacing={0.5}>
                  {[
                    { l: 'Orders',        v: period.orders },
                    { l: 'Delivered',     v: period.delivered,       c: '#00783e' },
                    { l: 'Spend',         v: fmt$(period.spend),     c: '#7b1fa2' },
                    { l: 'Coupon Savings',v: fmt$(period.coupon_savings) },
                    { l: 'COD Orders',    v: period.cod_orders },
                    { l: 'Wallet Orders', v: period.wallet_orders },
                  ].map(({ l, v, c }) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Referrals</Typography>
                <Stack spacing={0.5}>
                  {[
                    { l: 'Referral Code',      v: profile.referral_code || '—' },
                    { l: 'Total Referrals',    v: referrals.total_referrals },
                    { l: 'Successful',         v: referrals.successful_referrals, c: '#00783e' },
                    { l: 'Reward Earned',      v: fmt$(referrals.total_reward_earned), c: '#7b1fa2' },
                  ].map(({ l, v, c }) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223', fontFamily: l === 'Referral Code' ? 'monospace' : undefined }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: `1px solid ${pc.color}33` }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: pc.color, display: 'block', mb: 1 }}>Membership</Typography>
                <Stack spacing={0.5}>
                  {[
                    { l: 'Plan',         v: membership.plan_name },
                    { l: 'Subscribed',   v: fmtDate(membership.subscription_date) },
                    { l: 'Expiry',       v: fmtDate(membership.expiry), c: membership.is_expired ? '#c62828' : undefined },
                    { l: 'Billing',      v: membership.recurring_label || '—' },
                  ].map(({ l, v, c }) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 1: Performance */}
        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 2, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="body2" color="text.secondary">Overall Rating:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: ratingColor(performance.rating), ml: 1 }}>
                  {safeRating(performance.rating)}
                </Typography>
                <Chip size="small" label={`${pct(performance.success_rate)} success`}
                  sx={{ bgcolor: performance.success_rate >= 80 ? '#d1f4e0' : performance.success_rate >= 50 ? '#fff3e0' : '#ffebee',
                    color: performance.success_rate >= 80 ? '#00783e' : performance.success_rate >= 50 ? '#e65100' : '#c62828',
                    fontWeight: 700, fontSize: '11px', ml: 1 }} />
              </Box>
            </Grid>
            {[
              { label: 'All-Time Orders',   items: [
                { l: 'Total Orders',    v: performance.total_orders },
                { l: 'Delivered',       v: performance.total_delivered,       c: '#00783e' },
                { l: 'Cancelled',       v: performance.total_cancelled,       c: performance.total_cancelled > 0 ? '#c62828' : undefined },
                { l: 'Failed Delivery', v: performance.total_failed_delivery, c: performance.total_failed_delivery > 0 ? '#c62828' : undefined },
                { l: 'COD Orders',      v: performance.cod_orders },
                { l: 'Pay Later',       v: performance.pay_later_orders },
              ]},
              { label: 'Financials', items: [
                { l: 'Total Spend',     v: fmt$(performance.total_spend),         c: '#7b1fa2' },
                { l: 'Coupon Savings',  v: fmt$(performance.total_coupon_savings) },
                { l: 'Wallet Used',     v: fmt$(performance.total_wallet_used)    },
                { l: 'Reward Discount', v: fmt$(performance.total_reward_discount)},
                { l: 'First Order',     v: fmtDate(performance.first_order_date)  },
                { l: 'Last Order',      v: fmtDate(performance.last_order_date)   },
              ]},
              { label: 'Browsing', items: [
                { l: 'Total Views',        v: performance.total_views },
                { l: 'Purchase Events',    v: performance.total_purchase_events },
                { l: 'Unique Products',    v: performance.unique_products },
                { l: 'Categories',         v: performance.categories_browsed },
                { l: 'First Activity',     v: fmtDate(performance.first_activity) },
                { l: 'Last Activity',      v: fmtDate(performance.last_activity)  },
              ]},
            ].map(({ label, items }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5', height: '100%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>{label}</Typography>
                  <Stack spacing={0.5}>
                    {items.map(({ l, v, c }: any) => (
                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Tab 2: Recent Orders */}
        {tab === 2 && (
          recent_orders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Receipt sx={{ fontSize: 48, color: '#c9cccf', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No recent orders</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafbfb' }}>
                    {['Order', 'Status', 'Amount', 'Payment', 'Date', 'Delivered'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent_orders.map(o => (
                    <TableRow key={o.order_id} sx={{ '&:hover': { bgcolor: '#fafbfb' }, '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>#{o.order_id}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6d7175' }}>{o.invoice_no}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Chip size="small" label={o.status}
                          sx={{ bgcolor: o.status_code === 7 ? '#d1f4e0' : o.status_code === 8 ? '#ffebee' : '#e3f2fd',
                            color: o.status_code === 7 ? '#00783e' : o.status_code === 8 ? '#c62828' : '#1565c0',
                            fontWeight: 600, fontSize: '10px', height: '20px' }} />
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{fmt$(o.amount)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        {o.cod        && <Chip size="small" label="COD"    sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '9px', height: '16px', mr: 0.3 }} />}
                        {o.pay_later  && <Chip size="small" label="Later"  sx={{ bgcolor: '#e8eaf6', color: '#3f51b5', fontSize: '9px', height: '16px', mr: 0.3 }} />}
                        {o.wallet_used && <Chip size="small" label="Wallet" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontSize: '9px', height: '16px' }} />}
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="caption" color="text.secondary">{o.order_date}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="caption" color="text.secondary">{o.delivered_at || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {/* Tab 3: Device & Profile */}
        {tab === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Account Info</Typography>
                <Stack spacing={0.8}>
                  {[
                    { l: 'Registered',  v: account.registered_at },
                    { l: 'Last Active', v: account.last_active_at || '—' },
                    { l: 'Days Inactive',v: account.days_since_active !== null ? `${account.days_since_active} days` : '—' },
                    { l: 'Login Type',  v: account.login_type },
                    { l: 'Contact',     v: profile.contact || '—' },
                    { l: 'Country Code',v: profile.country_code || '—' },
                  ].map(({ l, v }) => (
                    <Box key={l} sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Device</Typography>
                <Stack spacing={0.8}>
                  {[
                    { l: 'Device Name', v: device.device_name  || '—' },
                    { l: 'OS Version',  v: device.os_version   || '—' },
                    { l: 'Model',       v: device.model_number || '—' },
                    { l: 'App Installed',v: device.app_installed ? 'Yes' : 'No', c: device.app_installed ? '#00783e' : '#c62828' },
                    { l: 'Push Enabled', v: device.push_enabled  ? 'Yes' : 'No', c: device.push_enabled  ? '#00783e' : '#c62828' },
                  ].map(({ l, v, c }: any) => (
                    <Box key={l} sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained"
          sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Web User Detail Dialog
// ─────────────────────────────────────────────────────────────────────────────

const WebUserDetailDialog: React.FC<{
  user: WebUserEntry | null;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const [tab, setTab] = useState(0);
  if (!user) return null;
  const { profile, today, period, performance, recent_orders } = user;
  const isGuest = profile.user_type === 'guest';

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '14px', maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              width: 42, height: 42,
              bgcolor: isGuest ? '#fff3e0' : '#e3f2fd',
              color: isGuest ? '#e65100' : '#1565c0',
              fontWeight: 700, fontSize: '16px',
            }}>
              {getInitials(profile.full_name)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>
                {profile.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isGuest ? 'Guest' : profile.email} · #{profile.user_id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip size="small"
            label={isGuest ? 'Guest User' : profile.account_status}
            sx={{
              bgcolor: isGuest ? '#fff3e0' : profile.account_status === 'Active' ? '#d1f4e0' : '#ffebee',
              color:   isGuest ? '#e65100' : profile.account_status === 'Active' ? '#00783e' : '#c62828',
              fontWeight: 600, fontSize: '11px',
            }} />
          <Chip size="small" label={profile.login_type}
            sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600, fontSize: '11px' }} />
          {profile.days_since_active !== null && (
            <Chip size="small" label={`${profile.days_since_active}d since active`}
              sx={{ bgcolor: profile.days_since_active > 30 ? '#ffebee' : '#e8f5e9',
                color: profile.days_since_active > 30 ? '#c62828' : '#2e7d32',
                fontWeight: 600, fontSize: '11px' }} />
          )}
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mt: 1.5,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px', minHeight: 40, color: '#6d7175' },
          '& .Mui-selected': { color: '#202223', fontWeight: 600 },
          '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 2 },
        }}>
          <Tab label="Overview" />
          <Tab label="Performance" />
          <Tab label={`Orders (${recent_orders.length})`} />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {/* Tab 0: Overview */}
        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Today</Typography>
                <Stack spacing={0.5}>
                  {[
                    { l: 'Orders',         v: today.orders },
                    { l: 'Delivered',      v: today.delivered,            c: '#00783e' },
                    { l: 'Spend',          v: fmt$(today.spend),          c: '#7b1fa2' },
                    { l: 'Product Views',  v: today.product_views },
                    { l: 'Purchase Events',v: today.purchase_events },
                  ].map(({ l, v, c }) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Period</Typography>
                <Stack spacing={0.5}>
                  {[
                    { l: 'Orders',         v: period.orders },
                    { l: 'Delivered',      v: period.delivered,           c: '#00783e' },
                    { l: 'Cancelled',      v: period.cancelled,           c: period.cancelled > 0 ? '#c62828' : undefined },
                    { l: 'Spend',          v: fmt$(period.spend),         c: '#7b1fa2' },
                    { l: 'COD Orders',     v: period.cod_orders },
                    { l: 'Unique Products',v: period.unique_products },
                  ].map(({ l, v, c }) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Account Info</Typography>
                <Grid container spacing={1}>
                  {[
                    { l: 'Registered',  v: profile.registered_at },
                    { l: 'Last Seen',   v: profile.last_seen_at || '—' },
                    { l: 'Login Type',  v: profile.login_type },
                    { l: 'Contact',     v: profile.contact || '—' },
                  ].map(({ l, v }) => (
                    <Grid item xs={6} key={l}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{v}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 1: Performance */}
        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 2, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="body2" color="text.secondary">Rating:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: ratingColor(performance.rating), ml: 1 }}>
                  {safeRating(performance.rating)}
                </Typography>
                <Chip size="small" label={`${pct(performance.success_rate)} success`}
                  sx={{ bgcolor: performance.success_rate >= 80 ? '#d1f4e0' : '#fff3e0',
                    color: performance.success_rate >= 80 ? '#00783e' : '#e65100',
                    fontWeight: 700, fontSize: '11px', ml: 1 }} />
              </Box>
            </Grid>
            {[
              { label: 'Orders', items: [
                { l: 'Total',     v: performance.total_orders },
                { l: 'Delivered', v: performance.total_delivered,  c: '#00783e' },
                { l: 'Cancelled', v: performance.total_cancelled,  c: performance.total_cancelled > 0 ? '#c62828' : undefined },
                { l: 'Total Spend',v: fmt$(performance.total_spend), c: '#7b1fa2' },
                { l: 'Coupon Savings',v: fmt$(performance.coupon_savings) },
              ]},
              { label: 'Browsing', items: [
                { l: 'Total Views',      v: performance.total_views },
                { l: 'Purchase Events',  v: performance.total_purchases },
                { l: 'Unique Products',  v: performance.unique_products },
                { l: 'Categories',       v: performance.categories_browsed },
                { l: 'First View',       v: fmtDate(performance.first_view) },
              ]},
            ].map(({ label, items }) => (
              <Grid item xs={12} sm={6} key={label}>
                <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5', height: '100%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>{label}</Typography>
                  <Stack spacing={0.5}>
                    {items.map(({ l, v, c }: any) => (
                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Tab 2: Orders */}
        {tab === 2 && (
          recent_orders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Receipt sx={{ fontSize: 48, color: '#c9cccf', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No recent orders</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafbfb' }}>
                    {['Order', 'Status', 'Amount', 'Date'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent_orders.map(o => (
                    <TableRow key={o.order_id} sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: '#fafbfb' } }}>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>#{o.order_id}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6d7175' }}>{o.invoice_no}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Chip size="small" label={o.status}
                          sx={{ bgcolor: o.status_code === 7 ? '#d1f4e0' : o.status_code === 8 ? '#ffebee' : '#e3f2fd',
                            color: o.status_code === 7 ? '#00783e' : o.status_code === 8 ? '#c62828' : '#1565c0',
                            fontWeight: 600, fontSize: '10px', height: '20px' }} />
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{fmt$(o.amount)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="caption" color="text.secondary">{o.order_date}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained"
          sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// App User Monitor Tab
// ─────────────────────────────────────────────────────────────────────────────

const AppUserTab: React.FC = () => {
  const [search,     setSearch]     = useState('');
  const [debSearch,  setDebSearch]  = useState('');
  const [page,       setPage]       = useState(1);
  const [detail,     setDetail]     = useState<AppUserEntry | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ── CSV Download ──
  const { download: downloadCSV, loading: csvLoading } = useCSVDownload(USER_MONITOR_ENDPOINTS.APP_USER_MONITOR);

  const handleDownloadCSV = () => {
    const params: Record<string, unknown> = {};
    if (debSearch) params.search = debSearch;
    downloadCSV(params, `app_user_monitor_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const query: AppUserMonitorRequest = useMemo(() => {
    const q: AppUserMonitorRequest = { page, limit: 10 };
    if (debSearch) q.search = debSearch;
    return q;
  }, [page, debSearch]);

  const { data, isLoading, isError, refetch } = useAppUserMonitorListQuery(query, { pollingInterval: 60000, refetchOnFocus: true });

  const users    = data?.data       || [];
  const summary  = data?.summary;
  const pagination = data?.pagination;
  const totalPages = pagination?.pages || 0;

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value); setPage(1);
    clearTimeout((window as any)._appUserSearch);
    (window as any)._appUserSearch = setTimeout(() => setDebSearch(e.target.value), 400);
  }, []);

  return (
    <Box>
      {/* Summary stats */}
      {!isLoading && !isError && summary && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { icon: <People sx={{ color: '#1565c0', fontSize: 20 }} />, bg: '#e3f2fd', value: summary.users.total,       label: 'Total Users'    },
              { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />, bg: '#e8f5e9', value: summary.users.active, label: 'Active'          },
              { icon: <Smartphone sx={{ color: '#7b1fa2', fontSize: 20 }} />, bg: '#f3e5f5', value: summary.users.app_installed, label: 'App Installed' },
              { icon: <NotificationsActive sx={{ color: '#e65100', fontSize: 20 }} />, bg: '#fff3e0', value: summary.users.push_enabled, label: 'Push Enabled' },
              { icon: <PersonAdd sx={{ color: '#00796b', fontSize: 20 }} />, bg: '#e0f2f1', value: summary.users.new_today, label: 'New Today'      },
              { icon: <PersonAdd sx={{ color: '#1565c0', fontSize: 20 }} />, bg: '#e3f2fd', value: summary.users.new_this_month, label: 'This Month' },
              { icon: <ShoppingCart sx={{ color: '#e65100', fontSize: 20 }} />, bg: '#fff3e0', value: summary.today.orders, label: "Today's Orders"  },
              { icon: <AttachMoney sx={{ color: '#7b1fa2', fontSize: 20 }} />, bg: '#f3e5f5', value: fmt$(summary.today.revenue), label: "Today's Revenue" },
            ].map(({ icon, bg, value, label }) => (
              <Grid item xs={6} sm={3} md={3} lg={1.5} key={label}>
                <StatCard icon={icon} bg={bg} value={value} label={label} small />
              </Grid>
            ))}
          </Grid>
          {/* Membership row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Subscribed',  value: summary.memberships.subscribed, color: '#1565c0', bg: '#e3f2fd' },
              { label: 'Silver',      value: summary.memberships.silver,     color: '#616161', bg: '#f5f5f5' },
              { label: 'Gold',        value: summary.memberships.gold,       color: '#f57f17', bg: '#fff8e1' },
              { label: 'Platinum',    value: summary.memberships.platinum,   color: '#3f51b5', bg: '#e8eaf6' },
            ].map(({ label, value, color, bg }) => (
              <Grid item xs={6} sm={3} key={label}>
                <Card sx={{ p: 2, borderRadius: '10px', border: `1px solid ${color}33`, bgcolor: bg, maxWidth: 320, width: '100%' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color }}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label} Members</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Search + Download */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search name, email, contact..."
          value={search} onChange={handleSearch}
          sx={{ maxWidth: 360, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }}
        />

        <Button
          size="small"
          variant="outlined"
          startIcon={csvLoading ? <CircularProgress size={14} sx={{ color: '#202223' }} /> : <Download sx={{ fontSize: 16 }} />}
          onClick={handleDownloadCSV}
          disabled={csvLoading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            borderRadius: '8px',
            borderColor: '#c9cccf',
            color: '#202223',
            bgcolor: 'white',
            px: 2,
            '&:hover': { borderColor: '#202223', bgcolor: '#f1f2f4' },
            '&:disabled': { opacity: 0.6 },
          }}
        >
          {csvLoading ? 'Downloading…' : 'Export CSV'}
        </Button>

        {pagination && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
            {pagination.total} user{pagination.total !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={30} sx={{ color: '#1565c0' }} /></Box>}
      {isError   && <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>Failed to load app user data.</Alert>}

      {/* Table */}
      {!isLoading && !isError && users.length > 0 && (
        <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid #e1e3e5', overflowX: 'auto' }}>
          {/* Header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '40px 48px minmax(0, 1fr) 140px 110px 90px 100px 100px 80px 70px', minWidth: 1030, gap: 1, px: 2, py: 1.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
            {['', 'ID', 'User', 'Plan', 'Status', 'Wallet', 'Total Spend', 'Success %', 'Rating', 'Action'].map(h => (
              <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase' }}>{h}</Typography>
            ))}
          </Box>

          {users.map(u => {
            const isExp = expandedId === u.profile.user_id;
            const pc    = planColor(u.membership.plan_name);
            return (
              <Box key={u.profile.user_id}>
                <Box
                  onClick={() => setExpandedId(isExp ? null : u.profile.user_id)}
                  sx={{ display: 'grid', gridTemplateColumns: '40px 48px minmax(0, 1fr) 140px 110px 90px 100px 100px 80px 70px', minWidth: 1030, gap: 1, px: 2, py: 1.8, alignItems: 'center', borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s', cursor: 'pointer' }}>
                  <IconButton size="small" sx={{ p: 0, color: '#6d7175' }}
                    onClick={e => { e.stopPropagation(); setExpandedId(isExp ? null : u.profile.user_id); }}>
                    {isExp ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>

                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#6d7175' }}>#{u.profile.user_id}</Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                      {getInitials(u.profile.full_name)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }} noWrap>{u.profile.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '11px' }}>{u.profile.email}</Typography>
                    </Box>
                  </Box>

                  <Chip size="small" label={u.membership.plan_name}
                    sx={{ bgcolor: pc.bg, color: pc.color, fontWeight: 700, fontSize: '11px', height: '22px', width: 'fit-content' }} />

                  <Chip size="small" label={u.account.status_label}
                    sx={{ bgcolor: u.account.status === 1 ? '#d1f4e0' : '#ffebee', color: u.account.status === 1 ? '#00783e' : '#c62828', fontWeight: 600, fontSize: '11px', height: '22px', width: 'fit-content' }} />

                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '12px' }}>{fmt$(u.wallet.balance)}</Typography>

                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223', fontSize: '12px' }}>{fmt$(u.performance.total_spend)}</Typography>

                  <Chip size="small" label={pct(u.performance.success_rate)}
                    sx={{ bgcolor: u.performance.success_rate >= 80 ? '#d1f4e0' : u.performance.success_rate >= 50 ? '#fff3e0' : '#ffebee',
                      color: u.performance.success_rate >= 80 ? '#00783e' : u.performance.success_rate >= 50 ? '#e65100' : '#c62828',
                      fontWeight: 700, fontSize: '11px', height: '22px', width: 'fit-content' }} />

                  <Typography variant="body2" sx={{ fontWeight: 700, color: ratingColor(u.performance.rating), fontSize: '12px' }}>
                    {safeRating(u.performance.rating)}
                  </Typography>

                  <Tooltip title="View Detail">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setDetail(u); }}
                      sx={{ color: '#1565c0', bgcolor: '#e3f2fd', borderRadius: '6px', p: 0.6, '&:hover': { bgcolor: '#bbdefb' } }}>
                      <Visibility sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Expanded */}
                <Collapse in={isExp} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                    <Grid container spacing={1.5}>
                      {[
                        { label: 'Today', items: [
                          { l: 'Orders', v: u.today.orders }, { l: 'Delivered', v: u.today.delivered, c: '#00783e' },
                          { l: 'Spend', v: fmt$(u.today.spend), c: '#7b1fa2' }, { l: 'Views', v: u.today.product_views },
                        ]},
                        { label: 'All-Time', items: [
                          { l: 'Orders', v: u.performance.total_orders }, { l: 'Delivered', v: u.performance.total_delivered, c: '#00783e' },
                          { l: 'Spend', v: fmt$(u.performance.total_spend), c: '#7b1fa2' }, { l: 'Wallet Used', v: fmt$(u.performance.total_wallet_used) },
                        ]},
                        { label: 'Device', items: [
                          { l: 'App', v: u.device.app_installed ? '✓ Installed' : '✗ Not Installed', c: u.device.app_installed ? '#00783e' : '#c62828' },
                          { l: 'Push', v: u.device.push_enabled ? '✓ Enabled' : '✗ Disabled', c: u.device.push_enabled ? '#00783e' : '#c62828' },
                          { l: 'Device', v: u.device.device_name || '—' }, { l: 'OS', v: u.device.os_version || '—' },
                        ]},
                        { label: 'Referrals', items: [
                          { l: 'Code', v: u.profile.referral_code || '—' },
                          { l: 'Total', v: u.referrals.total_referrals }, { l: 'Successful', v: u.referrals.successful_referrals, c: '#00783e' },
                          { l: 'Earned', v: fmt$(u.referrals.total_reward_earned), c: '#7b1fa2' },
                        ]},
                      ].map(({ label, items }) => (
                        <Grid item xs={12} sm={6} md={3} key={label}>
                          <Card sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 0.8 }}>{label}</Typography>
                            <Stack spacing={0.4}>
                              {items.map(({ l, v, c }: any) => (
                                <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" color="text.secondary">{l}</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Button size="small" startIcon={<Visibility />} onClick={() => setDetail(u)} sx={{ mt: 1.5, textTransform: 'none', color: '#202223', border: '1px solid #c9cccf', borderRadius: '6px' }}>Full Detail</Button>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {!isLoading && !isError && users.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <People sx={{ fontSize: 56, color: '#c9cccf', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No users found</Typography>
        </Box>
      )}

      {totalPages > 1 && !isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)}
            sx={{ '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
        </Box>
      )}

      <AppUserDetailDialog user={detail} onClose={() => setDetail(null)} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Web User Monitor Tab
// ─────────────────────────────────────────────────────────────────────────────

const WebUserTab: React.FC = () => {
  const [search,     setSearch]     = useState('');
  const [debSearch,  setDebSearch]  = useState('');
  const [userType,   setUserType]   = useState<'' | 'registered' | 'guest'>('');
  const [page,       setPage]       = useState(1);
  const [detail,     setDetail]     = useState<WebUserEntry | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ── CSV Download ──
  const { download: downloadCSV, loading: csvLoading } = useCSVDownload(USER_MONITOR_ENDPOINTS.WEB_USER_MONITOR);

  const handleDownloadCSV = () => {
    const params: Record<string, unknown> = {};
    if (debSearch) params.search = debSearch;
    if (userType)  params.user_type = userType;
    downloadCSV(params, `web_user_monitor_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const query: WebUserMonitorRequest = useMemo(() => {
    const q: WebUserMonitorRequest = { page, limit: 10 };
    if (debSearch) q.search = debSearch;
    if (userType)  q.user_type = userType;
    return q;
  }, [page, debSearch, userType]);

  const { data, isLoading, isError, refetch } = useWebUserMonitorListQuery(query, { pollingInterval: 60000, refetchOnFocus: true });

  const users      = data?.data       || [];
  const summary    = data?.summary;
  const pagination = data?.pagination;
  const totalPages = pagination?.pages || 0;

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value); setPage(1);
    clearTimeout((window as any)._webUserSearch);
    (window as any)._webUserSearch = setTimeout(() => setDebSearch(e.target.value), 400);
  }, []);

  return (
    <Box>
      {/* Summary */}
      {!isLoading && !isError && summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { icon: <People sx={{ color: '#1565c0', fontSize: 20 }} />, bg: '#e3f2fd', value: summary.registered.total,       label: 'Registered Users' },
            { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />, bg: '#e8f5e9', value: summary.registered.active, label: 'Active Registered' },
            { icon: <PersonAdd sx={{ color: '#00796b', fontSize: 20 }} />, bg: '#e0f2f1', value: summary.registered.new_today, label: 'New Today' },
            { icon: <Person sx={{ color: '#e65100', fontSize: 20 }} />, bg: '#fff3e0', value: summary.guests.total,           label: 'Guest Users' },
            { icon: <ShoppingCart sx={{ color: '#1565c0', fontSize: 20 }} />, bg: '#e3f2fd', value: summary.today.total_orders, label: "Today's Orders" },
            { icon: <AttachMoney sx={{ color: '#7b1fa2', fontSize: 20 }} />, bg: '#f3e5f5', value: fmt$(summary.today.total_revenue), label: "Today's Revenue" },
            { icon: <VisibilityIcon sx={{ color: '#00796b', fontSize: 20 }} />, bg: '#e0f2f1', value: summary.today.product_views, label: 'Product Views' },
            { icon: <Public sx={{ color: '#e65100', fontSize: 20 }} />, bg: '#fff3e0', value: summary.today.unique_visitors, label: 'Unique Visitors' },
          ].map(({ icon, bg, value, label }) => (
            <Grid item xs={6} sm={3} md={3} lg={1.5} key={label}>
              <StatCard icon={icon} bg={bg} value={value} label={label} small />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters + Download */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search name, email, contact..."
          value={search} onChange={handleSearch}
          sx={{ maxWidth: 360, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }}
        />
        {/* Type filter chips */}
        {(['', 'registered', 'guest'] as const).map(t => (
          <Chip key={t || 'all'} label={t === '' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
            onClick={() => { setUserType(t); setPage(1); }}
            sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '12px',
              bgcolor: userType === t ? '#202223' : '#f1f2f4',
              color:   userType === t ? 'white'    : '#6d7175',
              '&:hover': { bgcolor: userType === t ? '#000' : '#e1e3e5' },
            }}
          />
        ))}

        <Button
          size="small"
          variant="outlined"
          startIcon={csvLoading ? <CircularProgress size={14} sx={{ color: '#202223' }} /> : <Download sx={{ fontSize: 16 }} />}
          onClick={handleDownloadCSV}
          disabled={csvLoading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            borderRadius: '8px',
            borderColor: '#c9cccf',
            color: '#202223',
            bgcolor: 'white',
            px: 2,
            '&:hover': { borderColor: '#202223', bgcolor: '#f1f2f4' },
            '&:disabled': { opacity: 0.6 },
          }}
        >
          {csvLoading ? 'Downloading…' : 'Export CSV'}
        </Button>

        {pagination && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {pagination.total} user{pagination.total !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={30} sx={{ color: '#1565c0' }} /></Box>}
      {isError   && <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>Failed to load web user data.</Alert>}

      {/* Table */}
      {!isLoading && !isError && users.length > 0 && (
        <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid #e1e3e5', overflowX: 'auto' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '40px 48px 80px minmax(0, 1fr) 130px 100px 110px 90px 70px', minWidth: 912, gap: 1, px: 2, py: 1.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
            {['', 'ID', 'Type', 'User', 'Status', 'Total Spend', 'Success %', 'Rating', 'Action'].map(h => (
              <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase' }}>{h}</Typography>
            ))}
          </Box>

          {users.map(u => {
            const rowKey  = `${u.profile.user_type}_${u.profile.user_id}`;
            const isExp   = expandedId === rowKey;
            const isGuest = u.profile.user_type === 'guest';

            return (
              <Box key={rowKey}>
                <Box onClick={() => setExpandedId(isExp ? null : rowKey)}
                  sx={{ display: 'grid', gridTemplateColumns: '40px 48px 80px minmax(0, 1fr) 130px 100px 110px 90px 70px', minWidth: 912, gap: 1, px: 2, py: 1.8, alignItems: 'center', borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s', cursor: 'pointer' }}>

                  <IconButton size="small" sx={{ p: 0, color: '#6d7175' }}
                    onClick={e => { e.stopPropagation(); setExpandedId(isExp ? null : rowKey); }}>
                    {isExp ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>

                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#6d7175' }}>#{u.profile.user_id}</Typography>

                  <Chip size="small" label={isGuest ? 'Guest' : 'Reg.'}
                    sx={{ bgcolor: isGuest ? '#fff3e0' : '#e3f2fd', color: isGuest ? '#e65100' : '#1565c0', fontWeight: 700, fontSize: '10px', height: '20px', width: 'fit-content' }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <Avatar sx={{ width: 30, height: 30, bgcolor: isGuest ? '#fff3e0' : '#e3f2fd', color: isGuest ? '#e65100' : '#1565c0', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
                      {getInitials(u.profile.full_name)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }} noWrap>{u.profile.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '11px' }}>{u.profile.email || u.profile.contact || '—'}</Typography>
                    </Box>
                  </Box>

                  <Chip size="small" label={u.profile.account_status}
                    sx={{ bgcolor: u.profile.account_status === 'Active' || isGuest ? '#d1f4e0' : '#ffebee',
                      color: u.profile.account_status === 'Active' || isGuest ? '#00783e' : '#c62828',
                      fontWeight: 600, fontSize: '11px', height: '22px', width: 'fit-content' }} />

                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '12px' }}>{fmt$(u.performance.total_spend)}</Typography>

                  <Chip size="small" label={pct(u.performance.success_rate)}
                    sx={{ bgcolor: u.performance.success_rate >= 80 ? '#d1f4e0' : u.performance.success_rate >= 50 ? '#fff3e0' : '#ffebee',
                      color: u.performance.success_rate >= 80 ? '#00783e' : u.performance.success_rate >= 50 ? '#e65100' : '#c62828',
                      fontWeight: 700, fontSize: '11px', height: '22px', width: 'fit-content' }} />

                  <Typography variant="body2" sx={{ fontWeight: 700, color: ratingColor(u.performance.rating), fontSize: '12px' }}>
                    {safeRating(u.performance.rating)}
                  </Typography>

                  <Tooltip title="View Detail">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setDetail(u); }}
                      sx={{ color: '#1565c0', bgcolor: '#e3f2fd', borderRadius: '6px', p: 0.6, '&:hover': { bgcolor: '#bbdefb' } }}>
                      <Visibility sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Collapse in={isExp} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                    <Grid container spacing={1.5}>
                      {[
                        { label: 'Today', items: [
                          { l: 'Orders', v: u.today.orders }, { l: 'Delivered', v: u.today.delivered, c: '#00783e' },
                          { l: 'Spend', v: fmt$(u.today.spend), c: '#7b1fa2' }, { l: 'Views', v: u.today.product_views },
                        ]},
                        { label: 'All-Time', items: [
                          { l: 'Orders', v: u.performance.total_orders }, { l: 'Delivered', v: u.performance.total_delivered, c: '#00783e' },
                          { l: 'Spend', v: fmt$(u.performance.total_spend), c: '#7b1fa2' }, { l: 'Coupon Savings', v: fmt$(u.performance.coupon_savings) },
                        ]},
                        { label: 'Browsing', items: [
                          { l: 'Total Views', v: u.performance.total_views }, { l: 'Purchase Events', v: u.performance.total_purchases },
                          { l: 'Unique Products', v: u.performance.unique_products }, { l: 'Categories', v: u.performance.categories_browsed },
                        ]},
                        { label: 'Account', items: [
                          { l: 'Registered', v: u.profile.registered_at }, { l: 'Last Seen', v: u.profile.last_seen_at || '—' },
                          { l: 'Login Type', v: u.profile.login_type }, { l: 'Contact', v: u.profile.contact || '—' },
                        ]},
                      ].map(({ label, items }) => (
                        <Grid item xs={12} sm={6} md={3} key={label}>
                          <Card sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 0.8 }}>{label}</Typography>
                            <Stack spacing={0.4}>
                              {items.map(({ l, v, c }: any) => (
                                <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" noWrap>{l}</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223', textAlign: 'right' }}>{v}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Button size="small" startIcon={<Visibility />} onClick={() => setDetail(u)} sx={{ mt: 1.5, textTransform: 'none', color: '#202223', border: '1px solid #c9cccf', borderRadius: '6px' }}>Full Detail</Button>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {!isLoading && !isError && users.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <People sx={{ fontSize: 56, color: '#c9cccf', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No users found</Typography>
        </Box>
      )}

      {totalPages > 1 && !isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)}
            sx={{ '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
        </Box>
      )}

      <WebUserDetailDialog user={detail} onClose={() => setDetail(null)} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const UserMonitor = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mainTab,    setMainTab]    = useState(0);
  const [snackbar,   setSnackbar]   = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(v => !v)} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={() => setMobileOpen(v => !v)} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* Page header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#202223' }}>User Management/Monitor</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                  Track app users and web visitors — orders, activity & behaviour
                </Typography>
              </Box>
            </Box>

            {/* Main tabs card */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'visible' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{
                  px: 2,
                  '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 50, color: '#6d7175' },
                  '& .Mui-selected':      { color: '#202223', fontWeight: 700 },
                  '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
                }}>
                  <Tab icon={<PhoneAndroid sx={{ fontSize: 18 }} />} iconPosition="start" label="VOTO App Management" />
                  <Tab icon={<Web        sx={{ fontSize: 18 }} />} iconPosition="start" label="VOTO Website Management" />
                </Tabs>
              </Box>

              <Box sx={{ p: 3 }}>
                {mainTab === 0 && <AppUserTab />}
                {mainTab === 1 && <WebUserTab />}
              </Box>
            </Card>

          </Box>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserMonitor;